import { useState, useEffect, useCallback, useRef } from 'react';
import type { UseSnackbarProps, UseSnackbarReturn } from './types';

/**
 * Custom hook for Snackbar logic
 */
export const useSnackbar = ({
  open,
  onClose,
  autoHideDuration = 5000,
}: UseSnackbarProps): UseSnackbarReturn => {
  const [isVisible, setIsVisible] = useState(open);
  const onCloseRef = useRef(onClose);

  // Keep the ref up to date
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Sync visibility with the controlled `open` prop in both directions.
  // Previously this only handled `open` becoming true — a snackbar with no
  // close button and autoHideDuration=0 (e.g. a persistent loading toast)
  // had no code path that ever set isVisible back to false when the parent
  // flipped `open` to false, so it stayed on screen forever.
  useEffect(() => {
    setIsVisible(open);
  }, [open]);

  // Handle close - stable callback that doesn't depend on onClose
  const handleClose = useCallback(() => {
    setIsVisible(false);
    onCloseRef.current?.();
  }, []);

  // Auto-hide timer
  useEffect(() => {
    if (!open || autoHideDuration <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      handleClose();
    }, autoHideDuration);

    return () => clearTimeout(timer);
  }, [open, autoHideDuration, handleClose]);

  // Handle escape key
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose]);

  return {
    isVisible,
    handleClose,
  };
};
